import { Select as SelectA, type SelectProps, Spin } from 'antd';
import type { BaseOptionType, DefaultOptionType } from 'antd/lib/select';
import cn from 'classnames';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './select.module.css';

export type SelectProperties<ValueType, OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType> = {
  fullWidth?: boolean;
} & SelectProps<ValueType, OptionType>;

type SelectComponent = {
  Option?: typeof SelectA.Option;
} & (<ValueType, OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType>(
  props: SelectProperties<ValueType, OptionType>,
) => ReactElement);

const Select: SelectComponent = <ValueType, OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType>({
  classNames,
  fullWidth,
  loading,
  notFoundContent,
  placeholder,
  showSearch,
  ...props
}: SelectProperties<ValueType, OptionType>) => {
  const { t } = useTranslation();

  return (
    <SelectA<ValueType, OptionType>
      className={cn({ [styles['full-width'] ?? '']: fullWidth })}
      classNames={
        typeof classNames === 'function'
          ? (info) => {
              const resolvedClassNames = classNames(info);

              return {
                ...resolvedClassNames,
                popup: {
                  root: cn(resolvedClassNames?.popup, styles.popup),
                },
              };
            }
          : {
              ...classNames,
              popup: {
                root: cn(classNames?.popup, styles.popup),
              },
            }
      }
      loading={loading}
      notFoundContent={
        loading ? (
          <p className={styles.loading}>
            <Spin />
          </p>
        ) : (
          notFoundContent
        )
      }
      placeholder={typeof placeholder === 'string' ? t(placeholder) : placeholder}
      showSearch={
        showSearch
          ? {
              optionFilterProp: 'label',
              ...(typeof showSearch === 'object' ? showSearch : {}),
            }
          : showSearch
      }
      size="large"
      {...props}
    />
  );
};

Select.Option = SelectA.Option;

export default Select;
